import React, { useEffect, useState } from "react";
import {
  reduxForm,
  FormSection,
  getFormValues,
  getFormSyncErrors
} from "redux-form";
import AddressSellCheckout from "@components/forms/AddressSellCheckout";
import SellContactInformation from "./SellContactInformation";
import SecurityImages from "./SecurityImages";
import SellCardsDetails from "./SellCardsDetails";
import SellOrderSummary from "./SellOrderSummary";
import { completeSellOrder } from "@actions/sellOrders";
import { defaultAction } from "@actions/defaultAction";
import { postPrimaryAddress } from "@actions/customerInfo";
import { DISPLAY_MAIN_PAYMENT_ERROR } from "@constants/actionTypes";
import StyledCheckbox from "@components/forms/StyledCheckbox";
import loadable from "@loadable/component";
const SellVerificationCC = loadable(() =>
  import(
    /* webpackChunkName: "SellVerificationCC" */ "./forms/SellVerificationCC"
  )
);
import MainContentSellContainer from "./MainContentSellContainer";
import SellFormDesktopBottomButton from "./SellFormDesktopBottomButton";
import { verifyCC } from "@utils/validators/verifyCC";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

const MailCheckSellWorkflow = ({
  handleSubmit,
  submitFailed,
  dispatch,
  change,
  valid
}) => {
  const history = useHistory();
  const regularReduxDispatch = useDispatch();
  const mailCheckSell = useSelector(state =>
    getFormValues("mailCheckSell")(state)
  );
  const missingInfo = useSelector(state => state.customerInfo.missingInfo);
  const cardKnoxState = useSelector(state => state.cardKnox);
  const phone = useSelector(state => state.customerInfo.phone);
  const mainPaymentError = useSelector(
    state => state.customerInfo.mainPaymentError
  );
  const mailCheckSellErrors = useSelector(state =>
    getFormSyncErrors("mailCheckSell")(state)
  );
  const customerGetStatus = useSelector(
    state => state.customerInfo.customerGetStatus
  );

  const [displayPrimary, setDisplayPrimary] = useState(null);
  const [phoneCompleted, changePhoneCompleted] = useState(!!phone);
  const [openPrimaryForm, changeOpenPrimaryForm] = useState(false);
  const [openShippingForm, changeOpenShippingForm] = useState(false);
  const [openVerificationForm, changeOpenVerificationForm] = useState(false);
  const [openToEdit, changeOpenToEdit] = useState(false);

  useEffect(() => {
    if (customerGetStatus === "success") {
      if (!missingInfo) {
        setDisplayPrimary(false);
      } else {
        setDisplayPrimary(true);
      }
    }
  }, [customerGetStatus]);

  useEffect(() => {
    if (!missingInfo) {
      changeOpenToEdit(true);
    }
  }, [missingInfo]);

  useEffect(() => {
    if (shippingAddress && !shippingAddress.sameAsResidence) {
      changeOpenShippingForm(true);
    } else {
      changeOpenShippingForm(false);
    }
  }, [mailCheckSell?.shippingAddress?.sameAsResidence]);

  useEffect(() => {
    if (submitFailed && !mainPaymentError) {
      window.scrollTo(0, 0);
      regularReduxDispatch(
        defaultAction(
          DISPLAY_MAIN_PAYMENT_ERROR,
          "Please fix the errors and try again"
        )
      );
    }
  }, [submitFailed]);

  useEffect(() => {
    if (phoneCompleted) {
      if (missingInfo) {
        changeOpenPrimaryForm(true);
      }
    }
  }, [phoneCompleted]);

  useEffect(() => {
    if (openPrimaryForm) {
      if (openShippingForm) {
        changeOpenShippingForm(false);
      }
      if (openVerificationForm && missingInfo) {
        changeOpenVerificationForm(false);
      }
    }

    if (!openPrimaryForm) {
      if (!missingInfo) {
        changeOpenVerificationForm(true);
      }
    }
  }, [openPrimaryForm]);

  useEffect(() => {
    if (openShippingForm) {
      if (openPrimaryForm) {
        changeOpenPrimaryForm(false);
      }
      if (openVerificationForm && missingInfo) {
        changeOpenVerificationForm(false);
      }
    }

    if (!openShippingForm) {
      if (!missingInfo) {
        if (!openPrimaryForm) {
          changeOpenVerificationForm(true);
        }

        if (
          mailCheckSell &&
          !mailCheckSell.shippingAddress.sameAsResidence &&
          !hasAllAddressFields("shippingAddress")
        ) {
          change("shippingAddress.sameAsResidence", true);
        }
      }
    }
  }, [openShippingForm]);

  useEffect(() => {
    if (openVerificationForm) {
      if (openPrimaryForm) {
        changeOpenPrimaryForm(false);
      }
      if (openShippingForm) {
        changeOpenShippingForm(false);
      }
    }
  }, [openVerificationForm]);

  const submitMailCheckOrder = handleSubmit(() => {
    regularReduxDispatch(completeSellOrder(history, "mailCheckSell"));
  });

  const shippingAddress = mailCheckSell && mailCheckSell.shippingAddress;

  const hasAllAddressFields = name => {
    const fields = mailCheckSell[name];
    if (
      fields &&
      fields.fullname &&
      fields.firstname &&
      fields.lastname &&
      fields.street &&
      fields.city &&
      fields.state &&
      fields.zip
    ) {
      return true;
    } else {
      return false;
    }
  };

  const primaryContinueAction = async () => {
    const response = await regularReduxDispatch(
      postPrimaryAddress(mailCheckSell.primaryResidence)
    );
    return response;
  };

  return (
    <form onSubmit={submitMailCheckOrder}>
      {mailCheckSell && Object.keys(mailCheckSell).length > 0 && (
        <MainContentSellContainer>
          <div>
            <SellCardsDetails />
            <SellContactInformation
              errors={mailCheckSellErrors}
              phoneValue={mailCheckSell?.newPhone}
              changePhoneCompleted={changePhoneCompleted}
            />
            {displayPrimary && (
              <FormSection name="primaryResidence">
                <AddressSellCheckout
                  heading="Primary Residence"
                  phoneCompleted={phoneCompleted}
                  errors={mailCheckSellErrors.primaryResidence}
                  addressLineTwo={mailCheckSell?.primaryResidence?.street2}
                  formContent={mailCheckSell?.primaryResidence}
                  openForm={openPrimaryForm}
                  changeOpenForm={changeOpenPrimaryForm}
                  flowNumber={"2"}
                  openToEdit={openToEdit}
                  continueAction={primaryContinueAction}
                  displayPrimary={displayPrimary}
                />
              </FormSection>
            )}

            <FormSection name="shippingAddress">
              {shippingAddress && (
                <AddressSellCheckout
                  heading="Mailing Address"
                  errors={mailCheckSellErrors.shippingAddress}
                  addressLineTwo={mailCheckSell?.shippingAddress?.street2}
                  formContent={mailCheckSell?.shippingAddress}
                  openForm={openShippingForm}
                  changeOpenForm={changeOpenShippingForm}
                  sameAsParent={shippingAddress.sameAsResidence}
                  sameAsParentText={"Same as primary"}
                  topCheckboxName={"sameAsResidence"}
                  flowNumber={displayPrimary ? "3" : "2"}
                  openToEdit={openToEdit}
                  allFormInfo={mailCheckSell}
                  displayPrimary={displayPrimary}
                  changeReduxField={change}
                >
                  <StyledCheckbox
                    name={"sameAsResidence"}
                    text={"Same as primary residence."}
                  />
                </AddressSellCheckout>
              )}
            </FormSection>
            <FormSection name="verification">
              <SellVerificationCC
                openForm={openVerificationForm}
                heading={"Identity Verification"}
                errors={mailCheckSellErrors.verification}
                formContent={mailCheckSell?.verification}
                openToEdit={openToEdit}
                missingInfo={missingInfo}
                changeOpenForm={changeOpenVerificationForm}
                flowNumber={displayPrimary ? "4" : "3"}
                checkBoxText={
                  "The address associated with this credit card is the same as my shipping address"
                }
                checkBoxName={"sameAsShipping"}
                addressLineTwo={mailCheckSell?.shippingAddress?.street2}
                changeReduxField={change}
              />
            </FormSection>
            <SellFormDesktopBottomButton
              reduxFormDispatch={dispatch}
              valid={valid}
              missingInfo={
                missingInfo ||
                !verifyCC(mailCheckSell?.verification, cardKnoxState)
              }
            />
          </div>
          <div>
            <SellOrderSummary
              reduxFormDispatch={dispatch}
              valid={valid}
              missingInfo={
                missingInfo ||
                !verifyCC(mailCheckSell?.verification, cardKnoxState)
              }
            />
            <SecurityImages />
          </div>
        </MainContentSellContainer>
      )}
    </form>
  );
};

export default reduxForm({
  form: "mailCheckSell"
})(MailCheckSellWorkflow);
